/**
 * Capa tipada de la GESTIÓN DE MIEMBROS por proceso (gestor de participantes).
 *
 * Contrato del router backend/app/api/v1/miembros.py: listar quiénes están en
 * el proceso (con su origen de identidad), crear grupos, asignar rol/grupo y
 * quitar membresías. Quitar una membresía JAMÁS borra al usuario ni sus
 * aportes (C1: la evidencia ya aportada queda en las actas). El proceso nunca
 * puede quedar sin facilitadores (409 humano al degradar/quitar al último).
 *
 * Prefijo /api/v1 y Bearer los resuelve lib/api.ts.
 */
import { apiFetch } from './api';

/** Rol de la membresía en el proceso (enum RolMembresia del backend). */
export type RolMembresia = 'facilitador' | 'decisor' | 'participante' | 'observador';

export interface Miembro {
  user_id: string;
  nombre: string;
  email: string | null;
  rol: RolMembresia;
  grupo_id: string | null;
  grupo_nombre: string | null;
  /** Orígenes de identidad: 'sala' (auto-registro), 'google', 'local' (contraseña). */
  proveedores: string[];
}

export interface GrupoResumen {
  id: string;
  nombre: string;
  n_miembros: number;
}

export const miembrosApi = {
  /** Miembros del proceso con rol, grupo y origen de identidad. */
  listar: (procesoId: string) =>
    apiFetch<Miembro[]>(`/procesos/${procesoId}/miembros`),

  /** Grupos del proceso con su conteo de miembros. */
  grupos: (procesoId: string) =>
    apiFetch<GrupoResumen[]>(`/procesos/${procesoId}/grupos`),

  /** Facilitador: crea un grupo (409 si el nombre ya existe en el proceso). */
  crearGrupo: (procesoId: string, nombre: string) =>
    apiFetch<{ id: string; nombre: string }>(`/procesos/${procesoId}/grupos`, {
      method: 'POST',
      json: { nombre },
    }),

  /** Facilitador: cambia rol y/o grupo (grupo_id null desasigna). 409 si es la última persona facilitadora. */
  editar: (
    procesoId: string,
    userId: string,
    cuerpo: { rol?: RolMembresia; grupo_id?: string | null },
  ) =>
    apiFetch<Miembro>(`/procesos/${procesoId}/miembros/${userId}`, {
      method: 'PATCH',
      json: cuerpo,
    }),

  /** Facilitador: quita la MEMBRESÍA (no al usuario ni sus aportes). 409 si es la última facilitadora. */
  quitar: (procesoId: string, userId: string) =>
    apiFetch<void>(`/procesos/${procesoId}/miembros/${userId}`, {
      method: 'DELETE',
    }),
};

/** Llaves estables de TanStack Query. */
export const miembrosKeys = {
  lista: (procesoId: string) => ['miembros', procesoId] as const,
  grupos: (procesoId: string) => ['grupos', procesoId] as const,
};
